import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EntityId } from "@bbwt/interfaces";
import { BaseDataService, HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { IQueryCommand } from "@features/filter";
import { IPagedData } from "@features/grid";
import { OrganizationGroup } from "@mipro/custom-controls/organization-group";
import { DuplicatePatientCheckResult } from "./duplicate-patient";
import { PatientPageItem } from "./patient-page-item";
import { NotificationService } from "@main/notification/notification.service";
import { NotificationType } from "@main/notification/notification-type";
import { SetUTCTimeZone, SetLocalTimeZone } from "@bbwt/utils";
import { PlayerOfflineStorage } from "./player-offline.storage";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { PatientLogin } from "@main/users";
import { MessageService } from "primeng/api";
import { CreateSelfRegisterResult, ResultType } from "./create-self-register-result";
import { catchError, firstValueFrom, of, throwError } from "rxjs";
import { OrganizationSuggestion } from "@project/models";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { Message } from "@bbwt/classes";
import { AppStorage } from "@bbwt/utils/app-storage";
import { HistoricalItem } from "@mipro/classes/historical-item";

@Injectable({
    providedIn: "root"
})
export class PlayerService extends BaseDataService {
    readonly entityTitle = this.namingConvService.endUserSingular;
    readonly url = "api/end-user";
    isOnline: boolean = true;

    constructor(
        protected http: HttpClient,
        protected handlersFactory: HttpResponsesHandlersFactory,
        private notificationService: NotificationService,
        public namingConvService: NamingConventionService,
        private appOnlineStateService: AppOnlineStateService,
        private messageService: MessageService,
        private broadcastService: BroadcastService
    ) {
        super(http, handlersFactory);

        this.broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .subscribe(data => this.connectionChanged(data));
    }

    connectionChanged(data: { isAppOnline: boolean }) {
        this.isOnline = data.isAppOnline;
        if (this.isOnline) {
            this.saveOnlineAllSelfRegisterPlayer();
        }
    }

    async getFromPatient(patientId: EntityId) {
        const result = await this.handleRequest(
            this.http.get<PatientLogin>(`${this.url}/patient/${patientId}`),
            this.handlersFactory.getDefault()
        );

        result.dateOfBirth = SetLocalTimeZone(result.dateOfBirth);
        return result;
    }

    async getFromPatientId(patientId: EntityId) {
         const result = await this.handleRequest(
            this.http.get<PatientLogin>(`${this.url}/patient/id/${patientId}`),
            this.handlersFactory.getBypass()
        );

        result.dateOfBirth = SetLocalTimeZone(result.dateOfBirth);
        return result;
    }

    getOrganizationGroups(organizationId?: EntityId): Promise<OrganizationGroup[]> {
        const url = typeof organizationId === "string" ? `${this.url}/squads/${organizationId}` : `${this.url}/squads`;
        return this.handleRequest(this.http.get<OrganizationGroup[]>(url), this.handlersFactory.getDefault());
    }

    getOrganizationGroupsBySelfRegistrationUrl(selfRegistrationUrl: string): Promise<OrganizationGroup> {
        return this.handleRequest(
            this.http.get<OrganizationGroup>(`${this.url}/squads-by-self-registration-url/${selfRegistrationUrl}`),
            this.handlersFactory.getDefault()
        );
    }

    async getPage(
        queryCommand: IQueryCommand,
        responseHandlerSettings?: IHttpResponseHandlerSettings
    ): Promise<IPagedData<PatientPageItem>> {
        let page = await this.handleRequest<IPagedData<PatientPageItem>>(
            this.http.get<IPagedData<PatientPageItem>>(`${this.url}/page`, { params: this.constructHttpParams(queryCommand) }),
            this.handlersFactory.getForReadByFilter(responseHandlerSettings)
        );

        page.items = this.appOnlineStateService.isAppOnline ? page.items : page.items.filter(p => p.selfRegister != true);
        return page;
    }

    getHistoricalItems(patientId: EntityId): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-historical/${patientId}`),
            this.handlersFactory.getForReadAll());
    }


    archivePatient(patientId: string) {
        return this.handleRequest(
            this.http.delete(`${this.url}/patient/${patientId}`),
            this.handlersFactory.getForDelete("", { showSuccessMessage: false })
        );
    }

    async validatePatientData(
        patientId: EntityId,
        squadId: EntityId,
        responseHandlerSettings?: IHttpResponseHandlerSettings
    ): Promise<boolean> {
        return this.handleRequest<boolean>(this.http.get<boolean>(`${this.url}/validate-patient-data/${patientId}/${squadId}`));
    }

    async get(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings) {
        const patient = PlayerOfflineStorage.get(id as string);
        if (patient) {
            return patient;
        }

        const result = await this.handleRequest(
            this.http.get<PatientLogin>(`${this.url}/${id}`),
            this.handlersFactory.getForReadById(this.entityTitle, responseHandlerSettings)
        );

        if (result) {
            result.dateOfBirth = SetLocalTimeZone(result.dateOfBirth);
        }

        return result;
    }

    async checkDuplicatesById(patientId: EntityId): Promise<DuplicatePatientCheckResult> {
        return await this.handleRequest<DuplicatePatientCheckResult>(
            this.http.post<DuplicatePatientCheckResult>(`${this.url}/check-duplicates/${patientId}`, null),
            this.handlersFactory.getDefault()
        );
    }

    async create(patient: PatientLogin, skipDupCheck?: boolean) {
        if (this.appOnlineStateService.isAppOnline) {
            const url = this.buildDuplicateCheckUrl(this.url, skipDupCheck);

            const handlerSettings: IHttpResponseHandlerSettings = { showSuccessMessage: false, showErrorMessage: false };
            const handler = this.handlersFactory.getForCreate(this.entityTitle, handlerSettings);

            let result = await this.handleRequest(this.http.post<PatientLogin | DuplicatePatientCheckResult>(
                url, {...patient, dateOfBirth: SetUTCTimeZone(patient.dateOfBirth)}
            ), handler);

            if (
                !Array.isArray((<DuplicatePatientCheckResult>result).matches) &&
                !Array.isArray((<DuplicatePatientCheckResult>result).exactMatches) &&
                !Array.isArray((<DuplicatePatientCheckResult>result).fuzzyNameMatches)
            ) {
                handlerSettings.showSuccessMessage = true;
                handler.handleSuccess();
                const patientResult = <PatientLogin>result;
                patientResult.dateOfBirth = SetLocalTimeZone(patientResult.dateOfBirth);
            } else {
                (<DuplicatePatientCheckResult>result).matches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
                (<DuplicatePatientCheckResult>result).exactMatches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
                (<DuplicatePatientCheckResult>result).fuzzyNameMatches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
            }
            PlayerOfflineStorage.remove(patient.id);

            return result;
        } else {
            const existingPlayer = PlayerOfflineStorage.remove(patient.id);
            if (existingPlayer) {
                this.notificationService.deleteOfflineNotification(existingPlayer.offlineNotificationId);
            }
            patient.savedOffline = true;
            patient.id = null;
            patient.fullName = `${patient.firstName} ${patient.lastName}`;
            const savedOffline = PlayerOfflineStorage.save(patient);
            const notification = await this.notificationService.createOffline({
                id: null,
                title: `OFFLINE PATIENT ${patient.fullName} pending upload`,
                body: null,
                read: false,
                archived: false,
                created: new Date(),
                type: NotificationType.LocalPatient,
                redirectTo: "/app/patients",
                redirectParams: [
                    {
                        name: "patientId",
                        value: savedOffline.id
                    }
                ],
                savedOffline: true
            });

            savedOffline.offlineNotificationId = notification.id;
            return PlayerOfflineStorage.update(savedOffline);
        }
    }

    async createSelfRegisterPlayer(patient: PatientLogin, organizationURL?: string, skipDupCheck?: boolean, verificationNhsCode?: string) {
        const handlerSettings: IHttpResponseHandlerSettings = { showSuccessMessage: false };
        const handler = this.handlersFactory.getForCreate(this.entityTitle, handlerSettings);
        const selfRegisterUrl = organizationURL ? `self-register/${organizationURL}` : "self-register";
        const url = this.buildDuplicateCheckUrl(`${this.url}/${selfRegisterUrl}`, skipDupCheck, verificationNhsCode);

        if (this.appOnlineStateService.isAppOnline) {
            patient.dateOfBirth = SetUTCTimeZone(patient.dateOfBirth);
            const createResult = await this.handleRequest(this.http.post<CreateSelfRegisterResult>(url, patient));

            if (!createResult.success) {
                return createResult;
            }

            handler.handleSuccess();

            if (createResult.resultType === ResultType.Duplicates) {
                createResult.duplicates.matches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
                createResult.duplicates.exactMatches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
                createResult.duplicates.fuzzyNameMatches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
            }

            this.messageService.add(Message.Success(`Self-Register ${this.namingConvService.endUserSingular} Successfully Created.`));

            if (createResult.resultType === ResultType.LinkedToOrganization) {
                AppStorage.setItem(AppStorage.LoginInProcess, true);
            }

            return createResult;
        } else {
            const existingPlayer = PlayerOfflineStorage.remove(patient.id);
            if (existingPlayer) {
                this.notificationService.deleteOfflineNotification(existingPlayer.offlineNotificationId);
            }

            PlayerOfflineStorage.save({
                ...patient,
                savedOffline: true,
                id: null,
                fullName: `${patient.firstName} ${patient.lastName}`
            });
            return { success: true } as CreateSelfRegisterResult;
        }
    }

    async saveOnlineSelfRegisterPlayer(patientId: string, organizationURL?: string) {
        const patient = PlayerOfflineStorage.remove(patientId);
        patient.id = null;
        patient.savedOffline = null;
        return this.createSelfRegisterPlayer(patient, organizationURL);
    }

    async saveOnlineAllSelfRegisterPlayer(organizationURL?: string): Promise<void> {
        const patients = PlayerOfflineStorage.getAll().filter(p => p.selfRegister);
        for (const patient of patients) {
            await this.saveOnlineSelfRegisterPlayer(patient.id, organizationURL);
        }
    }

    async approveSelfRegisterPlayer(patientId: EntityId): Promise<void> {
        return await this.handleRequest<void>(
            this.http.post<void>(`${this.url}/self-register/${patientId}/approve`, null),
            this.handlersFactory.getDefault()
        );
    }

    async update(id: EntityId, patient: PatientLogin, skipDupCheck?: boolean) {
        const url = this.buildDuplicateCheckUrl(`${this.url}/${id}`, skipDupCheck);

        const patientCopy = { ...patient };

        patientCopy.dateOfBirth = SetUTCTimeZone(new Date(patientCopy.dateOfBirth));

        const handlerSettings: IHttpResponseHandlerSettings = { showSuccessMessage: false };
        const handler = this.handlersFactory.getForUpdate(this.entityTitle, handlerSettings);
        let result = await this.handleRequest(
            this.http.put<PatientLogin | DuplicatePatientCheckResult>(url, patientCopy).pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 404) {
                        return of({} as PatientLogin);
                    }

                    return throwError(() => error);
                })
            ),
            handler
        );

        if (result) {
            if (
                !Array.isArray((<DuplicatePatientCheckResult>result).matches) &&
                !Array.isArray((<DuplicatePatientCheckResult>result).fuzzyNameMatches)
            ) {
                handlerSettings.showSuccessMessage = true;
                handler.handleSuccess();
                const patientResult = <PatientLogin>result;
                patientResult.dateOfBirth = SetLocalTimeZone(patientResult.dateOfBirth);
            } else {
                (<DuplicatePatientCheckResult>result).matches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
                (<DuplicatePatientCheckResult>result).fuzzyNameMatches?.forEach(x => (x.dateOfBirth = SetLocalTimeZone(x.dateOfBirth)));
            }
        } else {
        }
        return result;
    }

    delete(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings) {
        const existingPlayer = PlayerOfflineStorage.remove(id.toString());
        if (existingPlayer) {
            this.notificationService.deleteOfflineNotification(existingPlayer.offlineNotificationId);
        } else {
            return this.handleRequest(
                this.http.delete<any>(`${this.url}/${id}`),
                this.handlersFactory.getForDelete(this.entityTitle, responseHandlerSettings)
            );
        }
    }

    deleteSelfReg(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<any> {
        return this.handleRequest<any>(
            this.http.delete<any>(`${this.url}/self-register/${id}/delete`),
            this.handlersFactory.getForDelete(this.entityTitle, responseHandlerSettings)
        );
    }

    private buildDuplicateCheckUrl(baseUrl: string, skipDupCheck?: boolean, nhsCode?: string) {
        const params = new URLSearchParams();
        if (skipDupCheck === true) params.append("skipDuplicatesCheck", "true");
        if (nhsCode) params.append("nhsCode", nhsCode);

        let q = params.toString();
        if (q) q = "?" + q;

        return baseUrl + q;
    }

    public restorePatientOrganisation(patientId: EntityId) {
        return this.handleRequest(
            this.http.post(`${this.url}/patient/${patientId}/restore-organization`, {}),
            this.handlersFactory.getDefault()
        );
    }

    addGroupsToPatient(id: EntityId, groupsIds: EntityId[]): Promise<any> {
        return this.handleRequest(
            this.http.put<any>(`${this.url}/patient/${id}/add-groups`, groupsIds),
            this.handlersFactory.getForUpdate("Duplicate Patient", { showSuccessMessage: true })
        );
    }

    getOrganizationsWithMedicalData(patientId: EntityId): Promise<OrganizationSuggestion[]> {
        return this.handleRequest(
            this.http.get<OrganizationSuggestion[]>(`${this.url}/${patientId}/data-organizations`),
            this.handlersFactory.getDefault()
        );
    }

    resendInvitation(patientLoginId: string): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/${patientLoginId}/resend-invitation`, null), this.handlersFactory.getDefault());
    }

    resendInvitationByPatientId(patientId: EntityId): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/${patientId}/send-invitation`, null), this.handlersFactory.getDefault());
    }
}
